import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isApiError } from '@shared/api';
import { generateFaq, getFaqModules, saveFaq, type FaqPreview } from '../api/faq';

export function faqErrorKey(error: unknown, hasPreview = false) {
  const uncertainKey = hasPreview ? 'faq.errors.uncertainSave' : 'faq.errors.uncertain';
  if (!isApiError(error)) return uncertainKey;
  if ([401, 403].includes(error.status)) return 'faq.errors.denied';
  if (
    error.code === 'APPLICATION_INTEGRATION_STALE_BASIS' ||
    error.status === 412 ||
    error.status === 428
  )
    return hasPreview ? 'faq.errors.stale' : 'faq.errors.staleGeneration';
  if (error.status === 404) return 'faq.errors.missing';
  if (/CONFIGURATION|KNOWLEDGE_UNAVAILABLE/.test(error.code))
    return hasPreview ? 'faq.errors.saveUnavailable' : 'faq.errors.configuration';
  if (/INVALID_OUTPUT|CONTEXT_TOO_LARGE|INVALID_PAYLOAD|PROVIDER_REJECTED/.test(error.code))
    return 'faq.errors.failed';
  if (!error.status || error.status >= 500 || error.status === 409) return uncertainKey;
  return 'faq.errors.failed';
}

function uncertain(error: unknown): boolean {
  if (isApiError(error) && error.code === 'FAQ_CONFIGURATION_UNAVAILABLE') return false;
  if (
    isApiError(error) &&
    /^APPLICATION_INTEGRATION_(INVALID_OUTPUT|STALE_BASIS|CONFIGURATION_UNAVAILABLE|KNOWLEDGE_UNAVAILABLE|CONTEXT_TOO_LARGE|INVALID_PAYLOAD|PROVIDER_REJECTED)$/.test(
      error.code
    )
  )
    return false;
  return !isApiError(error) || !error.status || error.status >= 500 || error.status === 409;
}

export function useFaqUpdate(scope: string, onSaved: () => void) {
  const client = useQueryClient();
  const modules = useQuery({
    queryKey: ['helpdesk-faq', scope, 'modules'],
    queryFn: ({ signal }) => getFaqModules(signal),
    retry: false,
    staleTime: 0,
  });
  const [selection, setSelection] = useState<string | null>(null);
  const moduleId =
    selection ??
    modules.data?.find(
      (item) => item.faq.audience === 'instructor' && item.faq.purpose === 'courses'
    )?.id ??
    '';
  const selected = modules.data?.find((item) => item.id === moduleId);
  const [preview, setPreview] = useState<FaqPreview | null>(null);
  const [pending, setPending] = useState<'generate' | 'save' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [recover, setRecover] = useState<'generate' | 'save' | null>(null);
  const busy = useRef(false);
  const generation = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
    };
  }, []);
  const current = useRef({ scope, moduleId });
  current.current = { scope, moduleId };
  const generate = async () => {
    if (busy.current || !selected || modules.error) return;
    busy.current = true;
    const run = ++generation.current;
    const target = moduleId;
    const identity = scope;
    const storageKey = `helpdesk-faq-preview:${identity}:${target}`;
    setPending('generate');
    setPreview(null);
    setError(null);
    setRecover(null);
    try {
      const key = sessionStorage.getItem(storageKey) ?? crypto.randomUUID();
      sessionStorage.setItem(storageKey, key);
      const result = await generateFaq(target, key);
      sessionStorage.removeItem(storageKey);
      if (
        mounted.current &&
        generation.current === run &&
        current.current.scope === identity &&
        current.current.moduleId === target
      )
        setPreview(result);
    } catch (failure) {
      if (!uncertain(failure)) sessionStorage.removeItem(storageKey);
      if (mounted.current && generation.current === run) {
        setError(failure);
        setRecover(uncertain(failure) ? 'generate' : null);
      }
    } finally {
      busy.current = false;
      if (mounted.current && generation.current === run) setPending(null);
    }
  };
  const save = async () => {
    if (busy.current || !preview || preview.output.moduleId !== moduleId) return;
    busy.current = true;
    const storageKey = `helpdesk-faq-save:${scope}:${preview.id}`;
    setPending('save');
    setError(null);
    setRecover(null);
    let committed = false;
    try {
      const key = sessionStorage.getItem(storageKey) ?? crypto.randomUUID();
      sessionStorage.setItem(storageKey, key);
      await saveFaq(preview, key);
      committed = true;
      sessionStorage.removeItem(storageKey);
    } catch (failure) {
      if (!uncertain(failure)) sessionStorage.removeItem(storageKey);
      if (mounted.current) {
        setError(failure);
        setRecover(uncertain(failure) ? 'save' : null);
      }
    } finally {
      busy.current = false;
      if (mounted.current) setPending(null);
    }
    if (committed) {
      // Cache refresh failure cannot turn an acknowledged commit into a failed Save.
      void client.invalidateQueries({ queryKey: ['helpdesk-faq', scope] });
      void client.invalidateQueries({ queryKey: ['content-owner-browse'] });
      void client.invalidateQueries({ queryKey: ['education-module'] });
      onSaved();
    }
  };
  const select = (id: string) => {
    if (busy.current) return;
    generation.current += 1;
    setSelection(id);
    setPreview(null);
    setError(null);
    setRecover(null);
  };
  return { modules, moduleId, selected, preview, pending, error, recover, generate, save, select };
}
